/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Autocomplete, Box, Tooltip, Typography } from '@mui/material';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { HiExclamationCircle } from 'react-icons/hi';
import Skeleton from '@mui/material/Skeleton';

import BaseInput from 'components/common/BaseInput';
import HelperDesc from 'components/common/WarningInputMessage/WarningInputMessage';
import LoadingButton from 'components/common/LoadingButton/LoadingButton';

import { RootState, AppDispatch } from 'redux/store';
import { createApp } from 'redux/application/application.middleware';
import { resetNewApp } from 'redux/application/application.slice';
import { loadApplications } from 'redux/applications/applications-middleware';
import api, { ApplicationPayload, AxiosResponse } from 'api';

import styles from './CreateCase.module.scss';

import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName } from 'utils/countries';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';

interface Case {
    country: string;
    visaProgram: string;
}

type Props = {
    closeModal: () => void;
};

const CreateCase = ({ closeModal }: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes, names: countryOptions } = useEnabledCountries();
    const soleCountry = chosenCountryName(codes);
    const [, setCaseCountries] = useState<string[]>([]);
    const [activeCountry, setActiveCountry] = useState<string | null>(soleCountry);
    const { id: candidateID } = useParams();

    const { data, error, isLoading } = useSelector((state: RootState) => state.expertiseList);
    const { newApp } = useSelector((state: RootState) => state.application);

    const { entity: consultant } = useSelector((state: RootState) => state.user);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [countryBasedProgramList, setCountryBasedProgramList] = useState<any[]>([]);

    useEffect(() => {
        if (candidateID && activeCountry) {
            getPrograms(candidateID, activeCountry);
        }
    }, [candidateID, activeCountry]);

    const getPrograms = async (userId: any, country: string) => {
        try {
            const resp = await api.programList.get(userId, country);
            if (resp?.status === 200 || resp?.status === 201) {
                // Handle both new enhanced format (objects) and old format (strings)
                const data = resp?.data;
                if (Array.isArray(data) && data.length > 0) {
                    // Check if it's the new enhanced format (objects with program_name)
                    if (typeof data[0] === 'object' && data[0].program_name) {
                        setCountryBasedProgramList(data);
                    } else {
                        // Old format (strings) - convert to new format for consistency
                        setCountryBasedProgramList(
                            data.map((program: string) => ({
                                program_name: program,
                                qualification_status: 'not_qualified',
                                display_color: 'black',
                            }))
                        );
                    }
                } else {
                    setCountryBasedProgramList([]);
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    // refactor needed
    // useEffect(() => {
    //     dispatch(loadExpertiseList());

    //     return () => {
    //         dispatch(resetNewApp());
    //     };
    // }, []);

    useEffect(() => {
        return () => {
            dispatch(resetNewApp());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (data) {
            const countries = Object.keys(data);
            setCaseCountries(countries);
        }
    }, [data]);

    useEffect(() => {
        reset((formValues) => ({
            ...formValues,
            caseStatus: '',
            visaProgram: '',
        }));

        // if (activeCountry && data) {
        //     const currentCountryPrograms: string[] = data[activeCountry as keyof typeof data];
        //     const withoutEmptyValue = currentCountryPrograms.filter((program) => program !== '');
        //     const onlyUniqueValues = [...new Set(withoutEmptyValue)];
        //     setVisaPrograms(onlyUniqueValues);
        //     return;
        // }
        // setVisaPrograms([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCountry, countryBasedProgramList]);

    useEffect(() => {
        if (newApp.entity) {
            setTimeout(() => {
                closeModal();
            }, 1500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newApp]);

    const {
        control,
        reset,
        setValue,
        formState: { errors, isDirty },
        handleSubmit,
    } = useForm<Case>({
        mode: 'all',
        defaultValues: {
            country: soleCountry ?? '',
            visaProgram: '',
        },
    });

    const submitForm: SubmitHandler<FieldValues> = async (data) => {
        if (!newApp.entity) {
            try {
                await createNewCase(data);

                setTimeout(() => {
                    closeModal();
                }, 1400);

                return;
            } catch (e) {
                console.error(e);
            }
        }
    };

    const createNewCase = async (data: FieldValues) => {
        if (candidateID) {
            const createPayload: ApplicationPayload = {
                candidate_id: Number(candidateID),
                consultant_id: consultant.id,
                program_name: data.visaProgram,
                country: data.country,
            };
            try {
                const resp = await dispatch(createApp(createPayload));
                const createdCase = (resp.payload as AxiosResponse)?.data;
                await dispatch(loadApplications(candidateID));
                navigate(
                    `/dashboard/sales-onboarding/agreement/${candidateID}/visa-program/${createdCase.id}`
                );
            } catch (e) {
                console.error(e);
            }
        }
    };

    useEffect(() => {
        if (!soleCountry) return;
        setActiveCountry((current) => current || soleCountry);
        setValue('country', soleCountry);
    }, [setValue, soleCountry]);

    const activeBtnCondition = isDirty && Object.keys(errors).length === 0;

    const tooltipText = (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'green',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">
                    {t('potentiallyQualified', { lng: language })}
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'orange',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">
                    {t('potentiallyQualifiedWithJobOffer', { lng: language })}
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'black',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">{t('allOtherPrograms', { lng: language })}</Typography>
            </Box>
        </Box>
    );

    return (
        <>
            {!error && !newApp.error && (
                <form className={styles.root} onSubmit={handleSubmit(submitForm)}>
                    <section className={styles['title-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <h2 className={styles.title}>
                                    {t('caseProperties', { lng: language })}
                                </h2>
                            </Skeleton>
                        ) : (
                            <h2 className={styles.title}>
                                {t('caseProperties', { lng: language })}
                            </h2>
                        )}
                    </section>

                    <div className={styles['text-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <p className={styles.text}>{t('caseCountry', { lng: language })}</p>
                            </Skeleton>
                        ) : (
                            <p className={styles.text}>{t('caseCountry', { lng: language })}</p>
                        )}
                    </div>

                    <div className={styles['input-wrapper']}>
                        {isLoading ? (
                            <Skeleton height={72} />
                        ) : (
                            <Controller
                                control={control}
                                name={'country'}
                                rules={{ required: t('requiredField', { lng: language }) }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        id="country"
                                        options={countryOptions}
                                        disablePortal
                                        value={value || null}
                                        onBlur={onBlur}
                                        renderOption={(props, option) => (
                                            <li {...props} style={{ margin: 0 }}>
                                                {option}
                                            </li>
                                        )}
                                        onChange={(_, data) => {
                                            onChange(data as string);
                                            setActiveCountry(data);
                                            return data;
                                        }}
                                        renderInput={(params) => (
                                            <BaseInput
                                                {...params}
                                                label={t('country', { lng: language })}
                                                error={error ? true : false}
                                                helperText={
                                                    error ? (
                                                        <HelperDesc
                                                            message={error.message?.toString()}
                                                        />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}
                    </div>

                    <div className={styles['text-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <p className={styles.text}>{t('service', { lng: language })}</p>
                            </Skeleton>
                        ) : (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <p className={styles.text}>{t('service', { lng: language })}</p>

                                <Tooltip title={tooltipText} placement="top" arrow>
                                    <InfoIcon sx={{ cursor: 'pointer', fontSize: 18 }} />
                                </Tooltip>
                            </Box>
                        )}
                    </div>

                    <div className={styles['input-wrapper']}>
                        {isLoading ? (
                            <Skeleton height={72} />
                        ) : (
                            <Controller
                                control={control}
                                name={'visaProgram'}
                                rules={{ required: t('requiredField', { lng: language }) }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        // freeSolo
                                        disabled={activeCountry ? false : true}
                                        fullWidth
                                        disablePortal
                                        id="program"
                                        value={value}
                                        onBlur={onBlur}
                                        onChange={(_, data) => {
                                            const programName =
                                                typeof data === 'string'
                                                    ? data
                                                    : data?.program_name;
                                            onChange(programName);
                                            return data;
                                        }}
                                        options={countryBasedProgramList}
                                        getOptionLabel={(option) => option.program_name || option}
                                        renderOption={(props, option) => (
                                            <li
                                                {...props}
                                                key={option.program_name || option}
                                                style={{
                                                    margin: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    color: option.display_color,
                                                }}
                                            >
                                                <span>{option.program_name || option}</span>
                                            </li>
                                        )}
                                        renderInput={(params: any) => (
                                            <BaseInput
                                                className={styles.input}
                                                {...params}
                                                label={t('service', { lng: language })}
                                                error={error ? true : false}
                                                helperText={
                                                    error ? (
                                                        <HelperDesc
                                                            message={error.message?.toString()}
                                                        />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}
                    </div>

                    <div className={styles.actions}>
                        {isLoading ? (
                            <Skeleton height={44} width={185} />
                        ) : (
                            <LoadingButton
                                isLoading={newApp.isLoading}
                                fulfilled={!!newApp.entity}
                                style={{ paddingLeft: '38px', paddingRight: '38px' }}
                                type="submit"
                                variant="contained"
                                size="min"
                                color="orange"
                                disabled={!activeBtnCondition}
                            >
                                {t('saveProperties', { lng: language })}
                            </LoadingButton>
                        )}
                    </div>
                </form>
            )}

            {(error || newApp.error) && (
                <div className={styles.error}>
                    <HiExclamationCircle className={styles['error__icon']} />
                    <h3 className={styles['error__text']}>{t('errorTitle', { lng: language })}</h3>
                    <span className={styles['error__desc']}>{newApp.error}.</span>
                </div>
            )}
        </>
    );
};
export default CreateCase;
